import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { Icon } from "@iconify/react";
import { useDispatch, useSelector } from "react-redux";
import { setAllPromo } from "../store/slices/user";
import { useConfig } from "../contexts/ConfigContext";
import PromotionCard from "../components/PromotionCard";
import s from "./Promotion.module.css";

const API_BASE_URL = (
  process.env.REACT_APP_API_BASE_URL || "https://api.bucket-001.online"
).replace(/\/+$/, "");

// Declare the shared promise at module scope
let promotionsFetchPromise = null;

const SKELETON_COUNT = 8;

// Inline SVG placeholder to avoid network calls and infinite onError loops
const makeSvgPlaceholder = (w, h, text = "Promotion Image") => {
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='${w}' height='${h}'><rect width='100%' height='100%' fill='#16120b'/><text x='50%' y='50%' dominant-baseline='middle' text-anchor='middle' fill='#d8b15a' font-family='sans-serif' font-size='16'>${text}</text></svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

const fmt = (n) => Number(n).toLocaleString("en-US");

// Admins separate conditions in Detail with "●": first part is the headline
const splitDetail = (detail) =>
  (detail || "")
    .split("●")
    .map((part) => part.trim())
    .filter(Boolean);

const bonusText = (p) => {
  const amt = Number(p?.BonusAmount);
  if (!amt) return "";
  const max = Number(p.BonusMaximum);
  if (p.BonusAmountType?.includes("%")) {
    return `โบนัส ${amt}%${max ? ` สูงสุด ${fmt(max)} บาท` : ""}`;
  }
  return `รับเพิ่ม ${fmt(amt)} บาท${max && max !== amt ? ` (สูงสุด ${fmt(max)})` : ""}`;
};

const depositText = (p) => {
  const min = p?.BonusDepMinimum;
  const max = p?.BonusDepMaximum;
  if (min == null) return "";
  if (max == null) return `ฝากขั้นต่ำ ${fmt(min)} บาท`;
  if (min === max) return `ฝาก ${fmt(min)} บาท`;
  return `ฝาก ${fmt(min)} - ${fmt(max)} บาท`;
};

const PromotionPopup = ({ promotion, image, onClose }) => {
  const closeRef = useRef(null);
  const [headline, ...conditions] = splitDetail(promotion.Detail);
  const deposit = depositText(promotion);
  const bonus = bonusText(promotion);

  useEffect(() => {
    const opener = document.activeElement;
    const onKey = (e) => e.key === "Escape" && onClose();

    // A class, not body.style: closed modals keep resetting inline overflow
    document.body.classList.add(s.scrollLock);
    document.addEventListener("keydown", onKey);
    closeRef.current?.focus();

    return () => {
      document.body.classList.remove(s.scrollLock);
      document.removeEventListener("keydown", onKey);
      opener?.focus?.();
    };
  }, [onClose]);

  return createPortal(
    <div className={s.overlay} onClick={onClose} role="presentation">
      <div
        className={s.dialog}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={headline || "รายละเอียดโปรโมชั่น"}
      >
        <button
          ref={closeRef}
          type="button"
          className={s.close}
          onClick={onClose}
          aria-label="ปิด"
        >
          <Icon icon="mdi:close" width={20} height={20} />
        </button>

        <div className={s.dialogScroll}>
          <div className={s.dialogMedia}>
            <img
              src={image}
              alt={headline}
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = makeSvgPlaceholder(800, 800);
              }}
            />
          </div>

          <div className={s.dialogBody}>
            <span className="lux-eyebrow">Promotion Details</span>
            <h2 className={s.dialogTitle}>{headline}</h2>

            {(deposit || bonus) && (
              <div className={s.facts}>
                {deposit && (
                  <span className={s.fact}>
                    <Icon
                      icon="solar:wallet-money-bold-duotone"
                      width="16"
                      height="16"
                    />
                    {deposit}
                  </span>
                )}
                {bonus && (
                  <span className={`${s.fact} ${s.factGold}`}>
                    <Icon
                      icon="solar:gift-bold-duotone"
                      width="16"
                      height="16"
                    />
                    {bonus}
                  </span>
                )}
              </div>
            )}

            {conditions.length > 0 && (
              <ul className={s.conditions}>
                {conditions.map((condition) => (
                  <li key={condition}>{condition}</li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

// Promotion component
const Promotion = () => {
  const dispatch = useDispatch();
  const { apiBaseUrl } = useConfig();
  const allPromo = useSelector((state) => state.user.allPromo);
  const [selectedPromotion, setSelectedPromotion] = useState(null);
  const [loading, setLoading] = useState(true);
  const promotionCount = allPromo?.length || 0;

  // Fetch promotions from the server
  useEffect(() => {
    let cancelled = false;

    if (!promotionsFetchPromise) {
      promotionsFetchPromise = axios
        .get(`${API_BASE_URL}/promotion/getavailable`)
        .finally(() => {
          promotionsFetchPromise = null;
        });
    }

    promotionsFetchPromise
      .then((res) => {
        if (cancelled) return;
        const list = res?.data?.data || res?.data || [];
        dispatch(setAllPromo(list));
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        dispatch(setAllPromo([]));
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  // Resolve image URL
  const resolveImg = (url) => {
    if (!url) return "";
    return url.startsWith("http") ? url : `${apiBaseUrl}${url}`;
  };
  const getImg = (promo, w = 800, h = 800) => {
    const url = promo?.imageUrl;
    return url ? resolveImg(url) : makeSvgPlaceholder(w, h);
  };

  const closePopup = React.useCallback(() => setSelectedPromotion(null), []);

  return (
    <section className={`lux-container ${s.page}`}>
      <header className="lux-head">
        <div className="lux-head__left">
          <span className="lux-eyebrow">Exclusive Offers</span>
          <h1 className="lux-head__title">
            โปรโมชั่น<b>ทั้งหมด</b>
          </h1>
          <p className={s.sub}>
            รวมโปรฝากรับโบนัสและสิทธิพิเศษที่เลือกใช้งานได้จากหน้าเดียว
          </p>
        </div>
        {promotionCount > 0 && (
          <span className={s.count}>
            <Icon icon="solar:gift-bold-duotone" width={15} height={15} />
            {promotionCount} รายการ
          </span>
        )}
      </header>

      <div className={s.grid}>
        {allPromo?.map((p) => (
          <PromotionCard
            key={p.PromotionID}
            promotion={p}
            title={splitDetail(p.Detail)[0] || ""}
            onClick={setSelectedPromotion}
            getImg={getImg}
            makeSvgPlaceholder={makeSvgPlaceholder}
            bonusText={bonusText}
            depositText={depositText}
          />
        ))}

        {promotionCount === 0 &&
          loading &&
          Array.from({ length: SKELETON_COUNT }, (_, i) => (
            <div key={i} className={s.skeleton} aria-hidden="true" />
          ))}

        {promotionCount === 0 && !loading && (
          <div className={s.empty}>
            <Icon icon="solar:gift-bold-duotone" width={44} height={44} />
            <h3>ไม่มีโปรโมชั่นในขณะนี้</h3>
            <p>เมื่อมีรายการใหม่ ระบบจะแสดงในหน้านี้โดยอัตโนมัติ</p>
          </div>
        )}
      </div>

      {selectedPromotion && (
        <PromotionPopup
          promotion={selectedPromotion}
          image={getImg(selectedPromotion)}
          onClose={closePopup}
        />
      )}
    </section>
  );
};

export default Promotion;
